import { useEffect, useRef, useState } from "react";
import { WipsViewModel } from "../../viewmodels/wips/WipsViewModel";
import { MagnifyingGlassIcon } from "@heroicons/react/24/outline";

import { connect } from "react-redux";

import * as actions from "../../store/actions/index";

import {
  formatDateWithTime,
  withRouter,
  isReadOnlyUser,
} from "../../shared/utility";
import { wipLabel } from "../../shared/tenantLabels";

import EmptyModal from "../../components/modals/EmptyModal";

import { useNavigate } from "react-router-dom";

import FilterDropdown from "../../components/filterDropdown/FilterDropdown";
import Pills, { PillType } from "../../components/controls/Pills";
import TableLoadingSkeleton from "../../components/shared/TableLoadingSkeleton";
import AuditActionLink from "../../components/audit/AuditActionLink";
import AuditHistoriesLink from "../../components/audit/AuditHistoriesLink";

import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import Button from "../../components/controls/Button";

function Wips(props) {
  const { addSnackbar, logout, tenant } = props;
  const wip = wipLabel(tenant);
  const {
    askDeleteWip,
    emptyModal,
    getWips,
    loading,
    wips,
    setEmptyModelOpen,
    showFilters,
    setShowFilters,
    filters,
    filterComponents,
    resetFilters,
    getRooms,
  } = WipsViewModel(logout, addSnackbar);

  const navigate = useNavigate();

  const isReadOnly = isReadOnlyUser();
  const [currentPage, setCurrentPage] = useState(1);
  const [loadingMore, setLoadingMore] = useState(false);

  useEffect(() => {
    getRooms();
    getWips(null, currentPage, filters);
  }, []);

  const searchRef = useRef();

  const handleSearch = () => {
    getWips(searchRef.current.value, 1, filters);
  };

  function navigateTo(target) {
    navigate(target);
  }

  const handleLoadMoreData = async () => {
    setLoadingMore(true);
    const nextPage = currentPage + 1;
    setCurrentPage(nextPage);
    await getWips(searchRef.current.value, nextPage, filters);
    setLoadingMore(false);
  };

  function dateBody(rowData) {
    return <p>{formatDateWithTime(rowData.created_date)}</p>;
  }

  function bodyStatus(rowData) {
    return (
      <div>
        <Pills
          content={{
            type: rowData.status === 0 ? PillType.Warning : PillType.Success,
            text: rowData.status === 0 ? "Open" : "Closed",
          }}
        />
      </div>
    );
  }

  function bodyActions(rowData) {
    return (
      <div className="flex flex-col gap-2 sm:flex-row sm:justify-end">
        <Button
          className="w-full sm:ml-5 sm:w-auto"
          to={"/wips/" + rowData.id}
        >
          {(!isReadOnly && "Edit") || "View"}
        </Button>
        {!isReadOnly && (
          <Button
            variant="danger"
            className="w-full sm:ml-2 sm:w-auto"
            onClick={() => {
              askDeleteWip(rowData);
            }}
          >
            Delete
          </Button>
        )}
        <AuditActionLink
          slug="wip"
          id={rowData.id}
          name={rowData.lot_number}
        />
      </div>
    );
  }

  return (
    <div>
      <div className="mt-2">
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="sm:flex sm:items-center px-4 py-5 sm:px-4 card-custom">
            <div className="sm:flex-auto">
              <h1 className="text-base font-semibold leading-6 text-gray-900">
                {`${wip}s`}
              </h1>
              <div className="mt-5 flex rounded-md">
                <div className="relative flex items-stretch flex-grow focus-within:z-10">
                  <input
                    type="search"
                    name="searchWips"
                    id="searchWips"
                    className="block w-full rounded-lg rounded-l-md pl-2 sm:text-sm border border-gray-300 rounded-tr-none rounded-br-none"
                    placeholder="Search by lot number"
                    ref={searchRef}
                    onKeyDown={(e) =>
                      e.key === "Enter" ? handleSearch() : null
                    }
                  />
                </div>
                <button
                  type="button"
                  className="-ml-px relative inline-flex items-center space-x-2 px-4 py-2 border border-gray-300 text-sm font-medium rounded-r-md text-gray-700 bg-gray-50 hover:bg-gray-100 focus:outline-none focus:ring-1"
                  onClick={() => handleSearch()}
                >
                  <MagnifyingGlassIcon
                    className="h-5 w-5 text-gray-400"
                    aria-hidden="true"
                  />
                </button>

                <FilterDropdown
                  actionCall={() =>
                    getWips(searchRef.current.value, 1, filters)
                  }
                  filterComponents={filterComponents}
                  resetFilters={() => {
                    resetFilters();
                  }}
                  showFilters={showFilters}
                  setShowFilters={setShowFilters}
                />

                <div>
                  {!isReadOnly && (
                    <Button
                      className="ml-2"
                      onClick={() => navigateTo("/wips/new")}
                    >
                      {`Create ${wip}`}
                    </Button>
                  )}
                  <AuditHistoriesLink slug="wip" />
                </div>
              </div>
            </div>
          </div>
          <div className="mt-8 flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                {loading ? (
                  <TableLoadingSkeleton rows={5} columns={4} />
                ) : (
                  <>
                    <div className="overflow-hidden card-custom">
                      <DataTable
                        value={wips.data}
                        dataKey="id"
                        tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                        cellClassName="p-2 border-1-bottom-gray text-sm"
                        rowClassName={(rowData) =>
                          rowData.cost_complete == 0 ? "bg-orange-50" : ""
                        }
                        editMode="row"
                      >
                        <Column header="Lot #" field="lot_number" />
                        <Column header="Room" field="room_name" />

                        <Column header="Date" body={dateBody} />

                        <Column header="Status" body={bodyStatus} />

                        <Column header="" body={bodyActions} />
                      </DataTable>
                    </div>
                    {wips.data.length < wips.total ? (
                      <div className="mt-4 text-center">
                        <Button
                          onClick={handleLoadMoreData}
                          loading={loadingMore}
                        >
                          Load More
                        </Button>
                      </div>
                    ) : (
                      <p className="mt-4 text-center text-gray-500">
                        No more wips to load
                      </p>
                    )}
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <EmptyModal
        childComponent={emptyModal.childComponent}
        open={emptyModal.open}
        setOpen={setEmptyModelOpen}
      />
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    tenant: state.auth.tenant,
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    logout: () => dispatch(actions.logout()),
    addSnackbar: (snackbar) => dispatch(actions.addSnackbar(snackbar)),
  };
};

export default withRouter(connect(mapStateToProps, mapDispatchToProps)(Wips));
